import React from 'react'
import classNames from 'classnames'
import { isNil } from 'lodash'
import pluralize from 'pluralize'

import { Pill } from '@/components/pills/Pill'

/**
 * The connections rail of the knowledge base's list-then-detail panel
 * (QCER-1027). Every row carries enough to tell one connection from another
 * without opening it: its provider, its operator-given name, the provider
 * identifier behind that name, whether it is live, and how fresh its content
 * is.
 *
 * @param {Array} props.connections rows to list, in display order
 * @param {number} props.selectedId the currently selected connection's id
 * @param {Function} props.onSelect connectionId => void
 * @param {React.ReactNode} props.addControl the add-connection affordance in the header
 * @param {boolean} props.creating the create form is open, so no real row is selected
 * @param {Array} props.drafts unsaved-connection rows, `{ key, name, icon, selected, onSelect }`
 *   each — the one being created plus any typed drafts preserved across row switches
 * @param {Function} props.primaryName row => the row's headline name
 * @param {Function} props.identifier row => the provider identifier under the name
 * @param {Function} props.rowIcon row => the row's provider icon, or null for none
 * @param {Function} props.suggestionCount row => how many notebooks the row has that are not
 *   indexed yet; a count above 0 shows as a badge after the name
 * @param {Function} props.alertLabel row => a warning to show on the row, or null for none
 * @param {string} props.testId list test id
 */
export const ConnectionList = ({
  connections,
  selectedId,
  onSelect,
  addControl,
  creating,
  drafts = [],
  primaryName,
  identifier,
  rowIcon = null,
  suggestionCount = () => 0,
  alertLabel = () => null,
  testId,
}) => (
  <div className="connection-list" data-testid={testId}>
    <div className="connection-list__header">
      {/* A section heading, not a form label — `.label-style` gives it the
          label's typography without claiming to label a control. */}
      <span className="label-style">Connections</span>
      {addControl}
    </div>
    <ul className="connection-list__rows">
      {connections.map((connection) => {
        const selected = !creating && connection.id === selectedId
        const notebookCount = suggestionCount(connection)
        const notebookLabel = `${pluralize('notebook', notebookCount, true)} not indexed`
        const alert = alertLabel(connection)
        return (
          <li key={connection.id}>
            <button
              type="button"
              className={classNames('connection-list__row', { 'is-selected': selected })}
              // Selection is otherwise conveyed by background colour alone.
              aria-current={selected}
              onClick={() => onSelect(connection.id)}
              data-testid={`${testId}-row-${connection.id}`}
            >
              <span className="connection-list__title">
                {!isNil(rowIcon) && (
                  <span className="connection-list__provider-icon">{rowIcon(connection)}</span>
                )}
                <span className="connection-list__name">{primaryName(connection)}</span>
                {notebookCount > 0 && (
                  <span className="tab-count" role="img" aria-label={notebookLabel}>
                    {notebookCount}
                  </span>
                )}
              </span>
              {/* An unnamed connection falls back to its identifier as the name;
                  printing the identifier again under it is just noise. */}
              {primaryName(connection) !== identifier(connection) && (
                <span className="connection-list__identifier small-text">
                  {identifier(connection)}
                </span>
              )}
              <span className="connection-list__meta">
                <Pill success={connection.active} critical={!connection.active} xs>
                  {connection.active ? 'Active' : 'Inactive'}
                </Pill>
                {!isNil(alert) && (
                  <Pill warning xs>
                    {alert}
                  </Pill>
                )}
                <span className="field-help small-text">
                  {isNil(connection.last_synced_at)
                    ? 'Never synced'
                    : `Synced ${new Date(connection.last_synced_at).toLocaleString()}`}
                </span>
              </span>
              {/* Erased content is invisible in the row's other fields — an
                  inactive purged connection looks exactly like one that was
                  merely paused, and re-activating it silently re-ingests. */}
              {!isNil(connection.purged_at) && (
                <span className="field-help small-text">
                  Documents erased {new Date(connection.purged_at).toLocaleString()}
                </span>
              )}
            </button>
          </li>
        )
      })}
      {/* Unsaved connections: the one being created plus any typed drafts the
          operator switched away from — their input is preserved, and clicking
          the row reopens the create form with it. Last, because a saved row
          joins the list in creation order. */}
      {drafts.map((draft) => (
        <li key={draft.key}>
          <button
            type="button"
            className={classNames('connection-list__row', { 'is-selected': draft.selected })}
            aria-current={draft.selected}
            onClick={draft.onSelect}
            data-testid={`${testId}-row-new-${draft.key}`}
          >
            <span className="connection-list__title">
              <span className="connection-list__provider-icon">{draft.icon}</span>
              <span className="connection-list__name">{draft.name}</span>
            </span>
            <span className="connection-list__meta">
              <Pill xs>Unsaved</Pill>
            </span>
          </button>
        </li>
      ))}
    </ul>
  </div>
)
